import { ONE_PIXEL, ZERO_PIXELS } from "common/constants/scaleFactors";
import type { SheetPixels, Squared } from "common/types/nominalNumbersTypes";

export class PathPoint {
  public readonly isVector2 = true;

  constructor(
    public x: SheetPixels = ZERO_PIXELS,
    public y: SheetPixels = ZERO_PIXELS,
  ) {}

  clone(): PathPoint {
    return new PathPoint(this.x, this.y);
  }

  copy(point: PathPoint): this {
    this.x = point.x;
    this.y = point.y;
    return this;
  }

  equals(point: PathPoint): boolean {
    return this.x === point.x && this.y === point.y;
  }

  set(x: SheetPixels, y: SheetPixels): this {
    this.x = x;
    this.y = y;
    return this;
  }

  toArray(
    array: ArrayLike<SheetPixels> & { [index: number]: SheetPixels },
    offset: number = 0,
  ): ArrayLike<SheetPixels> {
    array[offset] = this.x;
    array[offset + 1] = this.y;
    return array;
  }
}

class PathColor {
  public value: string = "";

  setStyle(style: unknown): void {
    this.value = String(style);
  }
}

interface Curve2 {
  readonly type: string;
  getPoint(t: number): PathPoint;
  getPoints(divisions?: number): PathPoint[];
}

function quadraticBezier<N extends number>(t: number, p0: N, p1: N, p2: N): N {
  const k = 1 - t;
  return (k * k * p0 + 2 * k * t * p1 + t * t * p2) as N;
}

function cubicBezier<N extends number>(t: number, p0: N, p1: N, p2: N, p3: N): N {
  const k = 1 - t;
  const k2 = k * k;
  const t2 = t * t;
  return (k2 * k * p0 + 3 * k2 * t * p1 + 3 * k * t2 * p2 + t2 * t * p3) as N;
}

export class LineCurve implements Curve2 {
  public readonly isLineCurve = true;
  public readonly type: string = "LineCurve";

  constructor(
    public v1: PathPoint = new PathPoint(),
    public v2: PathPoint = new PathPoint(),
  ) {}

  getPoint(t: number): PathPoint {
    if (t === 1) {
      return this.v2.clone();
    }

    const x = this.v1.x + (this.v2.x - this.v1.x) * t;
    const y = this.v1.y + (this.v2.y - this.v1.y) * t;
    return new PathPoint(x as SheetPixels, y as SheetPixels);
  }

  getPoints(divisions: number = 1): PathPoint[] {
    const points: PathPoint[] = [];
    for (let i = 0; i <= divisions; i++) {
      points.push(this.getPoint(i / divisions));
    }
    return points;
  }
}

export class QuadraticBezierCurve implements Curve2 {
  public readonly isQuadraticBezierCurve = true;
  public readonly type: string = "QuadraticBezierCurve";

  constructor(
    public v0: PathPoint = new PathPoint(),
    public v1: PathPoint = new PathPoint(),
    public v2: PathPoint = new PathPoint(),
  ) {}

  getPoint(t: number): PathPoint {
    return new PathPoint(
      quadraticBezier(t, this.v0.x, this.v1.x, this.v2.x),
      quadraticBezier(t, this.v0.y, this.v1.y, this.v2.y),
    );
  }

  getPoints(divisions: number = 12): PathPoint[] {
    const points: PathPoint[] = [];
    for (let i = 0; i <= divisions; i++) {
      points.push(this.getPoint(i / divisions));
    }
    return points;
  }
}

export class CubicBezierCurve implements Curve2 {
  public readonly isCubicBezierCurve = true;
  public readonly type: string = "CubicBezierCurve";

  constructor(
    public v0: PathPoint = new PathPoint(),
    public v1: PathPoint = new PathPoint(),
    public v2: PathPoint = new PathPoint(),
    public v3: PathPoint = new PathPoint(),
  ) {}

  getPoint(t: number): PathPoint {
    return new PathPoint(
      cubicBezier(t, this.v0.x, this.v1.x, this.v2.x, this.v3.x),
      cubicBezier(t, this.v0.y, this.v1.y, this.v2.y, this.v3.y),
    );
  }

  getPoints(divisions: number = 12): PathPoint[] {
    const points: PathPoint[] = [];
    for (let i = 0; i <= divisions; i++) {
      points.push(this.getPoint(i / divisions));
    }
    return points;
  }
}

export class EllipseCurve implements Curve2 {
  public readonly isEllipseCurve = true;
  public readonly type: string = "EllipseCurve";

  constructor(
    public aX: SheetPixels = ZERO_PIXELS,
    public aY: SheetPixels = ZERO_PIXELS,
    public xRadius: SheetPixels = ONE_PIXEL,
    public yRadius: SheetPixels = ONE_PIXEL,
    public aStartAngle: number = 0,
    public aEndAngle: number = Math.PI * 2,
    public aClockwise: boolean = false,
    public aRotation: number = 0,
  ) {}

  getPoint(t: number): PathPoint {
    const twoPi = Math.PI * 2;
    let deltaAngle = this.aEndAngle - this.aStartAngle;
    const samePoints = Math.abs(deltaAngle) < Number.EPSILON;

    while (deltaAngle < 0) {
      deltaAngle += twoPi;
    }
    while (deltaAngle > twoPi) {
      deltaAngle -= twoPi;
    }

    if (deltaAngle < Number.EPSILON) {
      deltaAngle = samePoints ? 0 : twoPi;
    }

    if (this.aClockwise && !samePoints) {
      deltaAngle = deltaAngle === twoPi ? -twoPi : deltaAngle - twoPi;
    }

    const angle = this.aStartAngle + t * deltaAngle;
    let x = this.aX + this.xRadius * Math.cos(angle);
    let y = this.aY + this.yRadius * Math.sin(angle);

    if (this.aRotation !== 0) {
      const cos = Math.cos(this.aRotation);
      const sin = Math.sin(this.aRotation);
      const tx = x - this.aX;
      const ty = y - this.aY;
      x = tx * cos - ty * sin + this.aX;
      y = tx * sin + ty * cos + this.aY;
    }

    return new PathPoint(x as SheetPixels, y as SheetPixels);
  }

  getPoints(divisions: number = 24): PathPoint[] {
    const points: PathPoint[] = [];
    for (let i = 0; i <= divisions; i++) {
      points.push(this.getPoint(i / divisions));
    }
    return points;
  }
}

export class Path {
  public readonly type: string = "Path";
  public curves: Curve2[] = [];
  public autoClose = false;
  public currentPoint = new PathPoint(ZERO_PIXELS, ZERO_PIXELS);

  moveTo(x: SheetPixels, y: SheetPixels): this {
    this.currentPoint.set(x, y);
    return this;
  }

  lineTo(x: SheetPixels, y: SheetPixels): this {
    const curve = new LineCurve(this.currentPoint.clone(), new PathPoint(x, y));
    this.curves.push(curve);
    this.currentPoint.set(x, y);
    return this;
  }

  quadraticCurveTo(aCPx: SheetPixels, aCPy: SheetPixels, aX: SheetPixels, aY: SheetPixels): this {
    const curve = new QuadraticBezierCurve(this.currentPoint.clone(), new PathPoint(aCPx, aCPy), new PathPoint(aX, aY));
    this.curves.push(curve);
    this.currentPoint.set(aX, aY);
    return this;
  }

  bezierCurveTo(
    aCP1x: SheetPixels,
    aCP1y: SheetPixels,
    aCP2x: SheetPixels,
    aCP2y: SheetPixels,
    aX: SheetPixels,
    aY: SheetPixels,
  ): this {
    const curve = new CubicBezierCurve(
      this.currentPoint.clone(),
      new PathPoint(aCP1x, aCP1y),
      new PathPoint(aCP2x, aCP2y),
      new PathPoint(aX, aY),
    );
    this.curves.push(curve);
    this.currentPoint.set(aX, aY);
    return this;
  }

  absarc(
    aX: SheetPixels,
    aY: SheetPixels,
    aRadius: SheetPixels,
    aStartAngle: number,
    aEndAngle: number,
    aClockwise: boolean,
  ): this {
    return this.absellipse(aX, aY, aRadius, aRadius, aStartAngle, aEndAngle, aClockwise);
  }

  absellipse(
    aX: SheetPixels,
    aY: SheetPixels,
    xRadius: SheetPixels,
    yRadius: SheetPixels,
    aStartAngle: number,
    aEndAngle: number,
    aClockwise: boolean,
    aRotation: number = 0,
  ): this {
    const curve = new EllipseCurve(aX, aY, xRadius, yRadius, aStartAngle, aEndAngle, aClockwise, aRotation);

    if (this.curves.length > 0) {
      const firstPoint = curve.getPoint(0);
      if (!firstPoint.equals(this.currentPoint)) {
        this.lineTo(firstPoint.x, firstPoint.y);
      }
    }

    this.curves.push(curve);
    this.currentPoint.copy(curve.getPoint(1));
    return this;
  }

  getPoints(divisions: number = 12): PathPoint[] {
    const points: PathPoint[] = [];
    let last: PathPoint | undefined;

    for (const curve of this.curves) {
      let resolution = divisions;
      if (curve instanceof EllipseCurve) {
        resolution = divisions * 2;
      } else if (curve instanceof LineCurve) {
        resolution = 1;
      }
      const curvePoints = curve.getPoints(resolution);

      for (const point of curvePoints) {
        if (last && last.equals(point)) {
          continue;
        }
        points.push(point);
        last = point;
      }
    }

    if (this.autoClose && points.length > 1 && !points[points.length - 1].equals(points[0])) {
      points.push(points[0].clone());
    }

    return points;
  }
}

export class Shape extends Path {
  public override readonly type: string = "Shape";
  public holes: Path[] = [];
}

function area(contour: PathPoint[]): Squared<SheetPixels> {
  let sum = 0;
  for (let p = contour.length - 1, q = 0; q < contour.length; p = q++) {
    sum += contour[p].x * contour[q].y - contour[q].x * contour[p].y;
  }
  return (sum * 0.5) as Squared<SheetPixels>;
}

function isClockWise(points: PathPoint[]): boolean {
  return area(points) < 0;
}

function isPointInsidePolygon(point: PathPoint, polygon: PathPoint[]): boolean {
  const polygonLength = polygon.length;
  let inside = false;

  for (let p = polygonLength - 1, q = 0; q < polygonLength; p = q++) {
    let edgeLowPoint = polygon[p];
    let edgeHighPoint = polygon[q];
    let edgeDx = edgeHighPoint.x - edgeLowPoint.x;
    let edgeDy = edgeHighPoint.y - edgeLowPoint.y;

    if (Math.abs(edgeDy) > Number.EPSILON) {
      if (edgeDy < 0) {
        edgeLowPoint = polygon[q];
        edgeHighPoint = polygon[p];
        edgeDx = -edgeDx;
        edgeDy = -edgeDy;
      }

      if (point.y < edgeLowPoint.y || point.y > edgeHighPoint.y) {
        continue;
      }

      if (point.y === edgeLowPoint.y) {
        if (point.x === edgeLowPoint.x) {
          return true;
        }
      } else {
        const perpendicular = edgeDy * (point.x - edgeLowPoint.x) - edgeDx * (point.y - edgeLowPoint.y);
        if (perpendicular === 0) {
          return true;
        }
        if (perpendicular < 0) {
          continue;
        }
        inside = !inside;
      }
    } else {
      if (point.y !== edgeLowPoint.y) {
        continue;
      }
      if (
        (edgeHighPoint.x <= point.x && point.x <= edgeLowPoint.x) ||
        (edgeLowPoint.x <= point.x && point.x <= edgeHighPoint.x)
      ) {
        return true;
      }
    }
  }

  return inside;
}

export class ShapePath {
  public readonly type: string = "ShapePath";
  public color = new PathColor();
  public subPaths: Path[] = [];
  public currentPath: Path | null = null;
  public userData?: unknown;

  moveTo(x: SheetPixels, y: SheetPixels): this {
    this.currentPath = new Path();
    this.subPaths.push(this.currentPath);
    this.currentPath.moveTo(x, y);
    return this;
  }

  lineTo(x: SheetPixels, y: SheetPixels): this {
    if (this.currentPath) {
      this.currentPath.lineTo(x, y);
    }
    return this;
  }

  quadraticCurveTo(aCPx: SheetPixels, aCPy: SheetPixels, aX: SheetPixels, aY: SheetPixels): this {
    if (this.currentPath) {
      this.currentPath.quadraticCurveTo(aCPx, aCPy, aX, aY);
    }
    return this;
  }

  bezierCurveTo(
    aCP1x: SheetPixels,
    aCP1y: SheetPixels,
    aCP2x: SheetPixels,
    aCP2y: SheetPixels,
    aX: SheetPixels,
    aY: SheetPixels,
  ): this {
    if (this.currentPath) {
      this.currentPath.bezierCurveTo(aCP1x, aCP1y, aCP2x, aCP2y, aX, aY);
    }
    return this;
  }

  toShapes(isCCW: boolean): Shape[] {
    const subPaths = this.subPaths;
    if (subPaths.length === 0) {
      return [];
    }

    const shapes: Shape[] = [];

    if (subPaths.length === 1) {
      const shape = new Shape();
      shape.curves = subPaths[0].curves;
      return [shape];
    }

    let holesFirst = !isClockWise(subPaths[0].getPoints());
    holesFirst = isCCW ? !holesFirst : holesFirst;

    const betterShapeHoles: Array<Array<{ h: Path; p: PathPoint }>> = [];
    const newShapes: Array<{ s: Shape; p: PathPoint[] } | undefined> = [];
    let newShapeHoles: Array<Array<{ h: Path; p: PathPoint }>> = [];
    let mainIndex = 0;

    newShapes[mainIndex] = undefined;
    newShapeHoles[mainIndex] = [];

    for (const subPath of subPaths) {
      const points = subPath.getPoints();
      let solid = isClockWise(points);
      solid = isCCW ? !solid : solid;

      if (solid) {
        if (!holesFirst && newShapes[mainIndex]) {
          mainIndex++;
        }

        const shape = new Shape();
        shape.curves = subPath.curves;
        newShapes[mainIndex] = { s: shape, p: points };

        if (holesFirst) {
          mainIndex++;
        }
        newShapeHoles[mainIndex] = [];
      } else {
        newShapeHoles[mainIndex].push({ h: subPath, p: points[0] });
      }
    }

    if (!newShapes[0]) {
      return subPaths.map((subPath) => {
        const shape = new Shape();
        shape.curves = subPath.curves;
        return shape;
      });
    }

    if (newShapes.length > 1) {
      let ambiguous = false;
      let toChange = 0;

      for (let shapeIndex = 0; shapeIndex < newShapes.length; shapeIndex++) {
        betterShapeHoles[shapeIndex] = [];
      }

      for (let shapeIndex = 0; shapeIndex < newShapes.length; shapeIndex++) {
        const shapeHoles = newShapeHoles[shapeIndex];

        for (const hole of shapeHoles) {
          let holeUnassigned = true;

          for (let targetIndex = 0; targetIndex < newShapes.length; targetIndex++) {
            const targetShape = newShapes[targetIndex];
            if (!targetShape) {
              continue;
            }

            if (isPointInsidePolygon(hole.p, targetShape.p)) {
              if (shapeIndex !== targetIndex) {
                toChange++;
              }

              if (holeUnassigned) {
                holeUnassigned = false;
                betterShapeHoles[targetIndex].push(hole);
              } else {
                ambiguous = true;
              }
            }
          }

          if (holeUnassigned) {
            betterShapeHoles[shapeIndex].push(hole);
          }
        }
      }

      if (toChange > 0 && !ambiguous) {
        newShapeHoles = betterShapeHoles;
      }
    }

    for (let index = 0; index < newShapes.length; index++) {
      const shape = newShapes[index];
      if (!shape) {
        continue;
      }

      shapes.push(shape.s);
      for (const hole of newShapeHoles[index]) {
        shape.s.holes.push(hole.h);
      }
    }

    return shapes;
  }
}
